import React, { useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import Logo from '../components/Logo';
import { getApiUrl } from '../config/api.config';

const ResetPassword = () => {
	const [searchParams] = useSearchParams();
	const rawToken = searchParams.get('token');
	const token = (() => {
		if (!rawToken) return null;
		try {
			return decodeURIComponent(rawToken.trim());
		} catch {
			return rawToken.trim();
		}
	})();
	const [password, setPassword] = useState('');
	const [confirmPassword, setConfirmPassword] = useState('');
	const [status, setStatus] = useState('idle'); // 'idle' | 'loading' | 'success' | 'error'
	const [message, setMessage] = useState('');
	const [error, setError] = useState('');

	const handleSubmit = async (e) => {
		e.preventDefault();
		setError('');

		if (password.length < 6) {
			setError('Password must be at least 6 characters');
			return;
		}
		if (password !== confirmPassword) {
			setError('Passwords do not match');
			return;
		}
		if (!token) {
			setError('Invalid reset link. Please use the link from your email.');
			return;
		}

		setStatus('loading');

		try {
			await axios.post(getApiUrl('auth/reset-password'), {
				token,
				newPassword: password
			});
			setStatus('success');
			setMessage('Your password has been reset. You can now sign in with your new password.');
		} catch (err) {
			setStatus('error');
			setError(
				err.response?.data?.message ||
					err.response?.data?.error ||
					err.message ||
					'Reset failed. The link may be invalid or expired. Please request a new reset link.'
			);
		}
	};

	if (!token && status !== 'success') {
		return (
			<div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 py-12 px-4 sm:px-6 lg:px-8">
				<div className="max-w-md w-full">
					<div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
						<div className="px-10 py-12">
							<div className="text-center mb-6">
								<Logo showText={true} width={80} height={80} className="mx-auto" />
							</div>
							<div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
								<p className="text-sm text-red-800">Missing reset token. Please use the link from your email.</p>
							</div>
							<Link
								to="/forgot-password"
								className="block w-full py-3 text-center bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700"
							>
								Request reset link
							</Link>
							<div className="text-center mt-4">
								<Link to="/login" className="text-sm text-blue-600 hover:text-blue-500">Back to sign in</Link>
							</div>
						</div>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 py-12 px-4 sm:px-6 lg:px-8">
			<div className="max-w-md w-full">
				<div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
					<div className="px-10 py-12">
						<div className="text-center mb-8">
							<div className="flex justify-center items-center mb-6">
								<Logo showText={true} width={80} height={80} className="justify-center" />
							</div>
							<h2 className="text-2xl font-bold text-gray-900 mb-2">Reset password</h2>
							<p className="text-gray-600 text-sm">
								Enter your new password below.
							</p>
						</div>

						{status === 'success' ? (
							<div className="space-y-6">
								<div className="bg-green-50 border border-green-200 rounded-lg p-4">
									<div className="flex items-center">
										<svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
											<path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
										</svg>
										<span className="text-sm font-medium text-green-800">{message}</span>
									</div>
								</div>
								<Link
									to="/login"
									className="block w-full py-3 px-4 text-center bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold rounded-lg shadow-lg transition duration-200"
								>
									Sign in
								</Link>
							</div>
						) : (
							<form className="space-y-6" onSubmit={handleSubmit}>
								{error && (
									<div className="bg-red-50 border border-red-200 rounded-lg p-4">
										<div className="flex items-center">
											<svg className="w-5 h-5 text-red-400 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
												<path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
											</svg>
											<span className="text-sm text-red-800">{error}</span>
										</div>
									</div>
								)}

								<div>
									<label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
										New password
									</label>
									<input
										id="password"
										name="password"
										type="password"
										required
										minLength={6}
										value={password}
										onChange={(e) => setPassword(e.target.value)}
										className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200 text-gray-900 placeholder-gray-400"
										placeholder="At least 6 characters"
									/>
								</div>
								<div>
									<label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
										Confirm new password
									</label>
									<input
										id="confirmPassword"
										name="confirmPassword"
										type="password"
										required
										minLength={6}
										value={confirmPassword}
										onChange={(e) => setConfirmPassword(e.target.value)}
										className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200 text-gray-900 placeholder-gray-400"
										placeholder="Re-enter password"
									/>
								</div>
								<button
									type="submit"
									disabled={status === 'loading'}
									className="w-full py-3 px-4 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold rounded-lg shadow-lg transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
								>
									{status === 'loading' ? (
										<span className="flex items-center justify-center">
											<svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
												<circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
												<path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
											</svg>
											Resetting...
										</span>
									) : (
										'Reset password'
									)}
								</button>
								<div className="text-center">
									<Link to="/login" className="text-sm font-medium text-blue-600 hover:text-blue-500">
										Back to sign in
									</Link>
								</div>
							</form>
						)}
					</div>
				</div>
			</div>
		</div>
	);
};

export default ResetPassword;
